import React, { useEffect, useState } from 'react'
import { actionAtom } from 'atoms/atoms'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Button, EmptyState, Icon, RowsSkeletonLoader } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/ThemeLottie'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useMerchantsQuery } from 'hooks/queries/useMerchantsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { MerchantSheet } from './components/MerchantSheet'
import { MerchantTable } from './components/MerchantTable'

export const MerchantsDetails = () => {
  const { t } = useTranslation()
  const [action, setAction] = useAtom(actionAtom)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const permissions = useMappedRoles()?.manageMerchants

  const canEdit = permissions?.canEdit

  const handleOpenMerchantCreateSheet = () => {
    setAction({
      ...action,
      actionId: ActionIds.MERCHANT_CREATE,
      onClose: () => {
        setIsSheetOpen(false)
      },
      header: t('createMerchant'),
      meta: {
        merchant: null,
      },
    })
    setIsSheetOpen(true)
  }

  const { data: { merchantList = [] } = {}, isError, isPending } = useMerchantsQuery()

  const handleRefresh = () => window.location.reload()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.MANAGE_MERCHANTS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  return (
    <>
      {(!!merchantList.length || isPending) && (
        <PageHeader
          description={t('tokenInfo')}
          {...(canEdit && {
            renderRightSection: (
              <Button onClick={handleOpenMerchantCreateSheet} iconElement={<Icon name="Plus" />}>
                {t('createMerchant')}
              </Button>
            ),
          })}
        />
      )}

      {/* Show empty state */}
      {!isPending && !isError && !merchantList.length && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="no-data" />}
          title={t('manageMerchants.emptyStateHeadline')}
          description={t('manageMerchants.emptyStateDescription')}
          PrimaryButton={
            canEdit && (
              <Button onClick={handleOpenMerchantCreateSheet}>
                {t('manageMerchants.emptyStateButtonText')}
              </Button>
            )
          }
        />
      )}

      {/* Show loading state */}
      {isPending && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {/* Show error state */}
      {isError && !isPending && (
        <EmptyState
          testId="error-state"
          LottiePlayer={<Lottie src="connection-error" />}
          title={t('oopsSomethingWentWrongContracted')}
          description={t('reloadPage')}
          PrimaryButton={
            <Button iconElement={<Icon name="ArrowsClockwise" />} onClick={handleRefresh}>
              {t('refresh')}
            </Button>
          }
        />
      )}

      {!isPending && !isError && !!merchantList?.length && <MerchantTable />}

      <MerchantSheet isSheetOpen={isSheetOpen} />
    </>
  )
}
